import React from 'react';
import { Plus, Zap, Puzzle, Trash2 } from 'lucide-react';

/**
 * Le rail des activités parallèles de la progression, période par période
 * (maquette validée prof 2026-08-25). Deux fils conducteurs utilisent ce
 * composant : les rituels d'automatismes (émeraude ⚡, colonne GAUCHE de la
 * frise) et les résolutions de problèmes (rose 🧩, colonne DROITE). Une
 * chip = UNE séance (titre + semaine) ; son badge « N att. » compte ses
 * contenus et ouvre le panneau détail partagé (PanneauContenus). Mobile :
 * les mêmes chips vivent dans BandeauAutomatismes.
 *
 * Les classes Tailwind des thèmes sont écrites EN CLAIR (purge JIT).
 */
export const THEMES_RAIL = {
    AUTOMATISME: {
        icone: '⚡',
        IconeVide: Zap,
        chip: 'bg-emerald-100 border-emerald-400',
        chipTitre: 'text-emerald-900',
        chipBadge: 'bg-emerald-500',
        chipSemaine: 'text-emerald-600',
        ligne: 'linear-gradient(to bottom, rgba(16,185,129,.55), rgba(110,231,183,.15))',
        pastille: 'bg-emerald-500 shadow-emerald-500/30',
        videTexte: 'aucun rituel',
        videClasse: 'text-emerald-500/70',
        ajoutTexte: 'Ajouter un rituel',
        ajoutClasse: 'border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50 text-emerald-600',
        supprTitle: 'Supprimer le rituel',
        bandeauBord: 'border-emerald-400 shadow-emerald-500/10',
        bandeauFond: 'linear-gradient(90deg, #ecfdf5, #d1fae5, #ecfdf5)',
        bandeauIcone: 'bg-emerald-500',
        bandeauTitre: 'text-emerald-900',
        bandeauLabel: 'Rituels',
    },
    RESOLUTION: {
        icone: '🧩',
        IconeVide: Puzzle,
        chip: 'bg-rose-100 border-rose-400',
        chipTitre: 'text-rose-900',
        chipBadge: 'bg-rose-500',
        chipSemaine: 'text-rose-600',
        ligne: 'linear-gradient(to bottom, rgba(244,63,94,.5), rgba(253,164,175,.15))',
        pastille: 'bg-rose-500 shadow-rose-500/30',
        videTexte: 'aucune résolution',
        videClasse: 'text-rose-500/70',
        ajoutTexte: 'Ajouter une résolution',
        ajoutClasse: 'border-rose-300 hover:border-rose-400 hover:bg-rose-50 text-rose-600',
        supprTitle: 'Supprimer la résolution',
        bandeauBord: 'border-rose-400 shadow-rose-500/10',
        bandeauFond: 'linear-gradient(90deg, #fff1f2, #ffe4e6, #fff1f2)',
        bandeauIcone: 'bg-rose-500',
        bandeauTitre: 'text-rose-900',
        bandeauLabel: 'Résolutions',
    },
};

const formatSemaine = (iso) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : `sem. du ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
};

export const nbContenus = (rituel) =>
    (rituel.attendus_details || []).length + (rituel.savoir_faires_details || []).length;

/** La chip-séance, partagée rail/bandeau. La corbeille apparaît au survol
 *  (motif group-hover de l'app) et passe par le modal de confirmation
 *  existant — la suppression d'un rituel, perdue au passage aux chips,
 *  revient ici (retour prof 2026-08-25). */
export const ChipRituel = ({ rituel, onOpen, onDelete, type = 'AUTOMATISME' }) => {
    const t = THEMES_RAIL[type] || THEMES_RAIL.AUTOMATISME;
    return (
        <div className="relative group">
            <div
                role="button"
                tabIndex={0}
                onClick={() => onOpen(rituel)}
                className={`w-full text-left border-2 rounded-xl px-3 py-2 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer ${t.chip}`}
            >
                <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-bold truncate ${t.chipTitre}`}>{t.icone} {rituel.title}</span>
                    <span className={`flex-shrink-0 text-[10px] font-black text-white rounded-full px-2 py-0.5 ${t.chipBadge}`}>
                        {nbContenus(rituel)} att.
                    </span>
                </div>
                <p className={`mt-0.5 text-[10px] font-semibold ${t.chipSemaine}`}>{formatSemaine(rituel.start)}</p>
            </div>
            {onDelete ? (
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(rituel); }}
                    title={t.supprTitle}
                    aria-label={t.supprTitle}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-red-200 text-red-400 hover:text-red-600 hover:bg-red-50 shadow flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all"
                >
                    <Trash2 size={12} />
                </button>
            ) : null}
        </div>
    );
};

const RailAutomatismes = ({ periode, rituels, onOpen, onAdd, onDelete, type = 'AUTOMATISME' }) => {
    const t = THEMES_RAIL[type] || THEMES_RAIL.AUTOMATISME;
    return (
        // Les chips sont RÉPARTIES sur toute la hauteur de la période (retour
        // prof 2026-08-25) : des stations le long du rail, pas un paquet en
        // haut — le rail accompagne visuellement les séquences de sa période.
        <div className="relative h-full flex flex-col items-center pt-2 pb-4">
            {/* la ligne du rail */}
            <div
                className="absolute left-1/2 -translate-x-1/2 top-10 bottom-4 w-0.5 rounded-full"
                style={{ background: t.ligne }}
            />
            <div className={`relative px-3.5 py-1 rounded-full text-white text-[11px] font-extrabold tracking-wider shadow-md ${t.pastille}`}>
                PÉRIODE {periode.numero}
            </div>
            <div className="relative flex-1 w-full max-w-[200px] flex flex-col justify-evenly gap-2 py-4">
                {rituels.map((r) => (
                    <ChipRituel key={r.id} rituel={r} onOpen={onOpen} onDelete={onDelete} type={type} />
                ))}
                {rituels.length === 0 && !onAdd ? (
                    <p className={`flex items-center justify-center gap-1 text-[10px] font-semibold ${t.videClasse}`}>
                        <t.IconeVide size={11} /> {t.videTexte}
                    </p>
                ) : null}
            </div>
            {onAdd ? (
                <button
                    onClick={onAdd}
                    className={`relative w-full max-w-[200px] flex items-center justify-center gap-1.5 bg-white/60 border-2 border-dashed rounded-xl px-2.5 py-1.5 text-[11px] font-semibold transition-all ${t.ajoutClasse}`}
                >
                    <Plus size={12} />
                    {t.ajoutTexte}
                </button>
            ) : null}
        </div>
    );
};

export default RailAutomatismes;
